<template>
  <div class="flex-1 overflow-auto p-4">
    <!-- QM §1.2, §5.3 — the settings route is reachable by URL even with the sidebar entry
         hidden, so both the account flag and the read permission are checked here. -->
    <p v-if="!isQueueManagementEnabled" class="text-slate-600">
      {{ $t('QUEUES_MGMT.DISABLED') }}
    </p>
    <p v-else-if="!canViewQueueSettings" class="text-slate-600">
      {{ $t('QUEUES_MGMT.FORBIDDEN') }}
    </p>
    <template v-else>
      <woot-button
        v-if="hasCreatePermission('Queue Settings')"
        color-scheme="success"
        class-names="button--fixed-top"
        icon="add-circle"
        @click="openAddPopup"
      >
        {{ $t('QUEUES_MGMT.HEADER_BTN_TXT') }}
      </woot-button>

      <div class="flex flex-row gap-4">
        <div class="w-[60%]">
          <p
            v-if="!uiFlags.isFetching && !records.length"
            class="flex h-full items-center flex-col justify-center"
          >
            {{ $t('QUEUES_MGMT.LIST.404') }}
          </p>
          <woot-loading-state
            v-if="uiFlags.isFetching"
            :message="$t('QUEUES_MGMT.LOADING')"
          />

          <!-- QM §5.3.1 — search and sort run on the fetched list; an account holds a
             handful of queues, so there is nothing to paginate or query server-side. -->
          <!-- QM §5.10 — a placeholder is not an accessible name; the label carries it. -->
          <label
            v-if="!uiFlags.isFetching && records.length"
            class="block mb-2"
          >
            <span class="sr-only">{{ $t('QUEUES_MGMT.LIST.SEARCH') }}</span>
            <input
              v-model="searchQuery"
              type="search"
              :placeholder="$t('QUEUES_MGMT.LIST.SEARCH')"
            />
          </label>

          <p
            v-if="
              !uiFlags.isFetching && records.length && !visibleQueues.length
            "
            class="text-slate-600 dark:text-slate-400"
          >
            {{ $t('QUEUES_MGMT.LIST.NO_RESULTS') }}
          </p>

          <table
            v-if="!uiFlags.isFetching && visibleQueues.length"
            class="woot-table"
          >
            <thead>
              <th
                v-for="column in columns"
                :key="column.label"
                :aria-sort="ariaSort(column)"
                scope="col"
              >
                <!-- QM §5.10 — sorting must be reachable by keyboard, so the control is a
                   real button rather than a click handler on the <th>. -->
                <button
                  v-if="column.sortKey"
                  type="button"
                  class="inline-flex items-center gap-1 bg-transparent border-0 p-0 font-inherit cursor-pointer"
                  @click="toggleSort(column.sortKey)"
                >
                  {{ column.label }}
                  <span aria-hidden="true">
                    {{ sortIndicator(column) }}
                  </span>
                </button>
                <template v-else>{{ column.label }}</template>
              </th>
            </thead>
            <tbody>
              <tr v-for="queue in visibleQueues" :key="queue.id">
                <td>{{ queue.name }}</td>
                <td>{{ queue.primary_team_name }}</td>
                <td>{{ queue.overflow_team_name || '—' }}</td>
                <td v-tooltip.top="memberBreakdown(queue)">
                  {{ memberCount(queue) }}
                </td>
                <td>{{ triggerSummary(queue) }}</td>
                <td>{{ formatTime(queue.updated_at) }}</td>
                <td class="button-wrapper">
                  <woot-button
                    v-if="hasEditPermission('Queue Settings')"
                    v-tooltip.top="$t('QUEUES_MGMT.FORM.EDIT_BUTTON_TOOLTIP')"
                    variant="smooth"
                    size="tiny"
                    color-scheme="secondary"
                    class-names="grey-btn"
                    icon="edit"
                    @click="openEditPopup(queue)"
                  />
                  <woot-button
                    v-if="hasDeletePermission('Queue Settings')"
                    v-tooltip.top="$t('QUEUES_MGMT.FORM.DELETE')"
                    variant="smooth"
                    color-scheme="alert"
                    size="tiny"
                    icon="dismiss-circle"
                    class-names="grey-btn"
                    :is-loading="loading[queue.id]"
                    @click="openDeletePopup(queue)"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="w-[34%]">
          <span v-dompurify-html="$t('QUEUES_MGMT.SIDEBAR_TXT')" />
        </div>
      </div>

      <woot-modal :show.sync="showAddPopup" :on-close="hideAddPopup">
        <queue-form
          :header-title="$t('QUEUES_MGMT.ADD.TITLE')"
          :submit-button-text="$t('QUEUES_MGMT.FORM.CREATE')"
          :submit-in-progress="uiFlags.isCreating"
          :on-submit="onCreate"
          @close="hideAddPopup"
        />
      </woot-modal>

      <woot-modal :show.sync="showEditPopup" :on-close="hideEditPopup">
        <queue-form
          v-if="showEditPopup"
          :header-title="$t('QUEUES_MGMT.EDIT.TITLE')"
          :submit-button-text="$t('QUEUES_MGMT.FORM.EDIT')"
          :submit-in-progress="uiFlags.isUpdating"
          :form-data="selectedQueue"
          :on-submit="onUpdate"
          @close="hideEditPopup"
        />
      </woot-modal>

      <woot-delete-modal
        :show.sync="showDeleteConfirmationPopup"
        :on-close="closeDeletePopup"
        :on-confirm="confirmDeletion"
        :title="$t('QUEUES_MGMT.DELETE.CONFIRM.TITLE')"
        :message="$t('QUEUES_MGMT.DELETE.CONFIRM.MESSAGE')"
        :message-value="deleteMessage"
        :confirm-text="$t('QUEUES_MGMT.DELETE.CONFIRM.YES')"
        :reject-text="$t('QUEUES_MGMT.DELETE.CONFIRM.NO')"
      />
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { format, parseISO } from 'date-fns';
import alertMixin from 'shared/mixins/alertMixin';
import hasPermissionMixin from 'dashboard/mixins/hasPermission';
// QM §1.2 — reused purely for its `isQueueManagementEnabled` computed.
import queueSupervisionMixin from 'dashboard/mixins/queueSupervision';
import QueueForm from './QueueForm.vue';

// Sort keys positionally paired with QUEUES_MGMT.LIST.TABLE_HEADER. The trigger summary
// is assembled for display only and the action column holds buttons, so neither sorts.
const SORT_KEYS = [
  'name',
  'primary_team_name',
  'overflow_team_name',
  'members',
  null,
  'updated_at',
  null,
];

export default {
  components: {
    QueueForm,
  },
  mixins: [alertMixin, hasPermissionMixin, queueSupervisionMixin],
  data() {
    return {
      loading: {},
      showAddPopup: false,
      showEditPopup: false,
      showDeleteConfirmationPopup: false,
      selectedQueue: {},
      searchQuery: '',
      sortKey: 'name',
      sortOrder: 'asc',
    };
  },
  computed: {
    ...mapGetters({
      records: 'queues/getQueues',
      uiFlags: 'queues/getUIFlags',
    }),
    columns() {
      return this.$t('QUEUES_MGMT.LIST.TABLE_HEADER').map((label, index) => ({
        label,
        sortKey: SORT_KEYS[index],
      }));
    },
    // QM §5.3.1 — search matches the columns an admin can actually read off the row.
    visibleQueues() {
      const term = this.searchQuery.trim().toLowerCase();
      const matches = term
        ? this.records.filter(queue =>
            [queue.name, queue.primary_team_name, queue.overflow_team_name]
              .filter(Boolean)
              .some(value => value.toLowerCase().includes(term))
          )
        : [...this.records];

      const direction = this.sortOrder === 'asc' ? 1 : -1;
      return matches.sort((left, right) => {
        const a = this.sortValue(left);
        const b = this.sortValue(right);
        if (a === b) return 0;
        return a > b ? direction : -direction;
      });
    },
    deleteMessage() {
      return ` ${this.selectedQueue.name}?`;
    },
  },
  mounted() {
    if (!this.isQueueManagementEnabled) {
      return;
    }
    this.$store.dispatch('queues/get');
    this.$store.dispatch('teams/get');
  },
  methods: {
    memberCount(queue) {
      return (
        (queue.primary_team_member_count || 0) +
        (queue.overflow_team_member_count || 0)
      );
    },
    memberBreakdown(queue) {
      return this.$t('QUEUES_MGMT.LIST.MEMBERS_BREAKDOWN', {
        primary: queue.primary_team_member_count || 0,
        overflow: queue.overflow_team_member_count || 0,
      });
    },
    formatTime(value) {
      return value ? format(parseISO(value), 'dd/MM/yy HH:mm') : '—';
    },
    // QM §5.10 — screen readers announce the sort state from aria-sort, not from the arrow glyph.
    ariaSort(column) {
      if (!column.sortKey || this.sortKey !== column.sortKey) return null;
      return this.sortOrder === 'asc' ? 'ascending' : 'descending';
    },
    sortIndicator(column) {
      if (this.sortKey !== column.sortKey) return '';
      return this.sortOrder === 'asc' ? '↑' : '↓';
    },
    sortValue(queue) {
      if (this.sortKey === 'members') return this.memberCount(queue);
      if (this.sortKey === 'updated_at') {
        return queue.updated_at ? parseISO(queue.updated_at).getTime() : 0;
      }
      return (queue[this.sortKey] || '').toLowerCase();
    },
    toggleSort(sortKey) {
      if (!sortKey) return;
      if (this.sortKey === sortKey) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
        return;
      }
      this.sortKey = sortKey;
      this.sortOrder = 'asc';
    },
    triggerSummary(queue) {
      const parts = [];
      if (queue.overflow_time_trigger_enabled) {
        parts.push(
          this.$t('QUEUES_MGMT.LIST.SUMMARY.TIME', {
            minutes: Math.round(
              (queue.overflow_time_trigger_seconds || 0) / 60
            ),
          })
        );
      }
      if (queue.overflow_sla_trigger_enabled) {
        parts.push(
          this.$t('QUEUES_MGMT.LIST.SUMMARY.SLA', {
            percent: queue.overflow_sla_trigger_percent,
          })
        );
      }
      if (queue.inactivity_auto_return_enabled) {
        parts.push(
          this.$t('QUEUES_MGMT.LIST.SUMMARY.AUTO_RETURN', {
            minutes: Math.round((queue.inactivity_timeout_seconds || 0) / 60),
          })
        );
      }
      return parts.length ? parts.join(' · ') : '—';
    },
    openAddPopup() {
      this.showAddPopup = true;
    },
    hideAddPopup() {
      this.showAddPopup = false;
    },
    openEditPopup(queue) {
      this.selectedQueue = queue;
      this.showEditPopup = true;
    },
    hideEditPopup() {
      this.showEditPopup = false;
    },
    openDeletePopup(queue) {
      this.selectedQueue = queue;
      this.showDeleteConfirmationPopup = true;
    },
    closeDeletePopup() {
      this.showDeleteConfirmationPopup = false;
    },
    async onCreate(queueInfo) {
      try {
        await this.$store.dispatch('queues/create', queueInfo);
        this.showAlert(this.$t('QUEUES_MGMT.ADD.API.SUCCESS_MESSAGE'));
        this.hideAddPopup();
      } catch (error) {
        // QM §3.1.1 — surface the specific validation message
        const message =
          error?.response?.data?.message ||
          this.$t('QUEUES_MGMT.ADD.API.ERROR_MESSAGE');
        this.showAlert(message);
      }
    },
    async onUpdate(queueInfo) {
      try {
        await this.$store.dispatch('queues/update', {
          id: this.selectedQueue.id,
          ...queueInfo,
        });
        this.showAlert(this.$t('QUEUES_MGMT.EDIT.API.SUCCESS_MESSAGE'));
        this.hideEditPopup();
      } catch (error) {
        // QM §3.1.1 — same as onCreate: surface the backend validation message.
        const message =
          error?.response?.data?.message ||
          this.$t('QUEUES_MGMT.EDIT.API.ERROR_MESSAGE');
        this.showAlert(message);
      }
    },
    confirmDeletion() {
      this.loading = { ...this.loading, [this.selectedQueue.id]: true };
      this.closeDeletePopup();
      this.deleteQueue(this.selectedQueue.id);
    },
    async deleteQueue(id) {
      try {
        await this.$store.dispatch('queues/delete', id);
        this.showAlert(this.$t('QUEUES_MGMT.DELETE.API.SUCCESS_MESSAGE'));
      } catch (error) {
        // QM §5.3.1 Backend refuses deletion while waiting tickets reside in the queue.
        const message =
          error?.response?.data?.error ||
          this.$t('QUEUES_MGMT.DELETE.API.ERROR_MESSAGE');
        this.showAlert(message);
      } finally {
        this.loading = { ...this.loading, [id]: false };
      }
    },
  },
};
</script>
